"use client";

import * as React from "react";
import { Menu, Search } from "lucide-react";
import { Button } from "../primitives/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "../primitives/dropdown-menu";
import { CommandPalette, type CommandItem } from "../patterns/command-palette";
import { ThemeToggle } from "../patterns/theme-toggle";
import { cn } from "../primitives/cn";

export type NavItem = {
  id: string;
  label: string;
  href: string;
  active?: boolean;
  icon?: React.ReactNode;
  /** Optional count badge (e.g. open orders). */
  count?: number | string;
  /** When true, count badge uses danger styling (e.g. exceptions). */
  alert?: boolean;
};

export type NavGroup = {
  id: string;
  label: string;
  items: NavItem[];
};

export type DashboardLayoutProps = {
  /** Brand mark initials or an image (e.g. "LX"). */
  brandMark?: React.ReactNode;
  /** Use a clear container for transparent image marks. */
  brandMarkTransparent?: boolean;
  brand: React.ReactNode;
  brandSubtitle?: string;
  /** Grouped nav (preferred). Falls back to flat `navItems`. */
  navGroups?: NavGroup[];
  navItems?: NavItem[];
  onNavigate: (href: string) => void;
  /** Topbar crumb — section (uppercase) + page title. */
  pageCrumb?: { section: string; page: string };
  /** Workspace / Logistics Branch switcher — sidebar footer. */
  workspaceSwitcher?: React.ReactNode;
  notificationBell?: React.ReactNode;
  announcementBanner?: React.ReactNode;
  systemBanner?: React.ReactNode;
  /** Dropdown body for the profile pill (settings, sign out, …). */
  userMenu?: React.ReactNode;
  /** Profile pill display. */
  profile?: {
    name: string;
    role?: string;
    initials?: string;
  };
  /** Optional org / context pill in the topbar (before theme). */
  contextSwitcher?: React.ReactNode;
  /** Optional slot next to theme (e.g. language). */
  headerExtras?: React.ReactNode;
  /** Show theme toggle (default true). */
  showThemeToggle?: boolean;
  commandItems?: CommandItem[];
  onCommandSelect?: (item: CommandItem) => void;
  commandLabels?: {
    defaultGroup?: string;
    placeholder?: string;
    empty?: string;
    closeAria?: string;
  };
  searchPlaceholder?: string;
  a11yLabels?: {
    mainNav?: string;
    openMenu?: string;
    closeMenu?: string;
    close?: string;
    toggleSidebar?: string;
    search?: string;
    themeLight?: string;
    themeDark?: string;
    profileMenu?: string;
  };
  children: React.ReactNode;
};

function initialsFromName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return `${parts[0]![0] ?? ""}${parts[1]![0] ?? ""}`.toUpperCase();
}

/**
 * DashboardLayout — V3 app shell:
 * sidebar (brand, grouped nav, branch footer) + topbar
 * (burger, page crumb, search, theme, bell, profile).
 */
export function DashboardLayout({
  brandMark = "LX",
  brandMarkTransparent = false,
  brand,
  brandSubtitle,
  navGroups,
  navItems = [],
  onNavigate,
  pageCrumb,
  workspaceSwitcher,
  notificationBell,
  announcementBanner,
  systemBanner,
  userMenu,
  profile,
  contextSwitcher,
  headerExtras,
  showThemeToggle = true,
  commandItems = [],
  onCommandSelect,
  commandLabels,
  searchPlaceholder,
  a11yLabels,
  children,
}: DashboardLayoutProps) {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [collapsed, setCollapsed] = React.useState(false);
  const [cmdOpen, setCmdOpen] = React.useState(false);

  const groups: NavGroup[] = React.useMemo(() => {
    if (navGroups && navGroups.length > 0) return navGroups;
    return [{ id: "main", label: "", items: navItems }];
  }, [navGroups, navItems]);

  const activeItem = React.useMemo(() => {
    for (const g of groups) {
      const hit = g.items.find((i) => i.active);
      if (hit) return { group: g, item: hit };
    }
    return null;
  }, [groups]);

  const crumbSection =
    pageCrumb?.section ??
    (activeItem?.group.label
      ? activeItem.group.label.toUpperCase()
      : undefined);
  const crumbPage = pageCrumb?.page ?? activeItem?.item.label ?? "";

  const toggleSidebar = () => {
    if (typeof window !== "undefined" && window.matchMedia("(min-width: 1024px)").matches) {
      setCollapsed((v) => !v);
      return;
    }
    setMobileOpen((v) => !v);
  };

  const renderNav = (opts: { collapsedRail: boolean; onItemClick?: () => void }) => (
    <nav
      className={cn(
        "flex flex-1 flex-col gap-6 overflow-y-auto overflow-x-hidden",
        opts.collapsedRail ? "px-2" : "px-3",
      )}
      aria-label={a11yLabels?.mainNav ?? "Main"}
    >
      {groups.map((group) => (
        <div key={group.id} className={cn(opts.collapsedRail && "mb-0")}>
          {group.label && !opts.collapsedRail ? (
            <p className="mb-2 px-2 text-[11px] font-semibold uppercase tracking-wider text-tertiary">
              {group.label}
            </p>
          ) : null}
          <div className="flex flex-col gap-0.5">
            {group.items.map((item) => (
              <button
                key={item.id}
                type="button"
                title={opts.collapsedRail ? item.label : undefined}
                onClick={() => {
                  onNavigate(item.href);
                  opts.onItemClick?.();
                }}
                className={cn(
                  "relative flex w-full items-center gap-2 rounded-sm text-left text-sm font-medium transition-colors duration-micro",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  opts.collapsedRail
                    ? "justify-center px-0 py-2.5"
                    : "px-2 py-2",
                  item.active
                    ? "bg-primary-soft text-primary-soft-foreground"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground",
                )}
              >
                {item.icon ? (
                  <span className="flex size-4 shrink-0 items-center justify-center [&_svg]:size-4">
                    {item.icon}
                  </span>
                ) : null}
                {!opts.collapsedRail ? (
                  <>
                    <span className="min-w-0 flex-1 truncate">{item.label}</span>
                    {item.count != null && item.count !== "" ? (
                      <span
                        className={cn(
                          "ml-auto rounded-full px-2 py-px text-[11px] font-semibold",
                          item.alert
                            ? "bg-destructive text-destructive-foreground"
                            : item.active
                              ? "bg-primary text-primary-foreground"
                              : "bg-muted text-muted-foreground",
                        )}
                      >
                        {item.count}
                      </span>
                    ) : null}
                  </>
                ) : null}
                {opts.collapsedRail && item.active ? (
                  <span
                    className="absolute bottom-2 right-0 top-2 w-[3px] rounded-l-sm bg-primary"
                    aria-hidden
                  />
                ) : null}
              </button>
            ))}
          </div>
        </div>
      ))}
    </nav>
  );

  const brandBlock = (collapsedRail: boolean) => (
    <div
      className={cn(
        "flex shrink-0 items-center gap-2",
        collapsedRail ? "justify-center px-0 pb-4 pt-2" : "px-2 pb-6 pt-2",
      )}
    >
      <div
        className={cn(
          "flex size-[26px] shrink-0 items-center justify-center rounded-sm text-[13px] font-semibold",
          brandMarkTransparent
            ? "bg-transparent"
            : "bg-primary text-primary-foreground",
        )}
      >
        {brandMark}
      </div>
      {!collapsedRail ? (
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold leading-tight">{brand}</div>
          {brandSubtitle ? (
            <div className="truncate text-[11px] text-tertiary">{brandSubtitle}</div>
          ) : null}
        </div>
      ) : null}
    </div>
  );

  const profileInitials =
    profile?.initials ??
    (profile?.name ? initialsFromName(profile.name) : "?");

  return (
    <div className="flex h-dvh flex-col overflow-hidden bg-background">
      {systemBanner}
      {announcementBanner}
      <div
        className={cn(
          "grid min-h-0 flex-1 transition-[grid-template-columns] duration-panel ease-out",
          collapsed
            ? "lg:grid-cols-[64px_minmax(0,1fr)]"
            : "lg:grid-cols-[232px_minmax(0,1fr)]",
          "grid-cols-1",
        )}
      >
        {/* Desktop sidebar */}
        <aside
          className={cn(
            "hidden min-h-0 flex-col overflow-hidden border-r border-border bg-card lg:flex",
            collapsed ? "w-16 px-2 py-3" : "w-sidebar px-3 py-4",
          )}
        >
          {brandBlock(collapsed)}
          {renderNav({ collapsedRail: collapsed })}
          {workspaceSwitcher && !collapsed ? (
            <div className="mt-auto shrink-0 border-t border-border px-2 pb-1 pt-3">
              {workspaceSwitcher}
            </div>
          ) : null}
        </aside>

        {/* Mobile drawer */}
        {mobileOpen ? (
          <div className="fixed inset-0 z-[70] lg:hidden">
            <button
              type="button"
              className="absolute inset-0 bg-foreground/35"
              aria-label={a11yLabels?.closeMenu ?? "Close menu"}
              onClick={() => setMobileOpen(false)}
            />
            <aside className="absolute inset-y-0 left-0 flex w-sidebar flex-col bg-card px-3 py-4 shadow-lg animate-fade-in">
              {brandBlock(false)}
              {renderNav({
                collapsedRail: false,
                onItemClick: () => setMobileOpen(false),
              })}
              {workspaceSwitcher ? (
                <div className="mt-auto shrink-0 border-t border-border px-2 pb-1 pt-3">
                  {workspaceSwitcher}
                </div>
              ) : null}
            </aside>
          </div>
        ) : null}

        <div className="flex min-h-0 min-w-0 flex-col">
          <header className="z-40 flex h-14 shrink-0 items-center gap-3 border-b border-border bg-card px-3 md:px-4">
            <div className="flex min-w-0 shrink items-center gap-2">
              <Button
                type="button"
                size="icon"
                variant="ghost"
                className="size-8 shrink-0 text-muted-foreground"
                onClick={toggleSidebar}
                aria-label={
                  a11yLabels?.toggleSidebar ??
                  a11yLabels?.openMenu ??
                  "Toggle sidebar"
                }
                aria-expanded={mobileOpen || !collapsed}
              >
                <Menu className="size-[18px]" />
              </Button>
              {(crumbSection || crumbPage) && (
                <div className="hidden min-w-0 flex-col justify-center leading-tight sm:flex">
                  {crumbSection ? (
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-tertiary">
                      {crumbSection}
                    </span>
                  ) : null}
                  {crumbPage ? (
                    <span className="truncate text-sm font-semibold text-foreground">
                      {crumbPage}
                    </span>
                  ) : null}
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={() => setCmdOpen(true)}
              className={cn(
                "flex min-w-0 flex-1 items-center gap-2 rounded-md border border-border bg-background px-3 py-1.5 text-tertiary",
                "max-w-[360px] transition-colors duration-micro hover:border-border-strong",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                "mr-auto",
              )}
              aria-label={a11yLabels?.search ?? "Search"}
            >
              <Search className="size-[15px] shrink-0" aria-hidden />
              <span className="hidden min-w-0 flex-1 truncate text-left text-sm sm:inline">
                {searchPlaceholder ?? commandLabels?.placeholder ?? "Search…"}
              </span>
              <kbd className="ml-auto hidden shrink-0 rounded border border-border bg-card px-[5px] py-px text-[11px] text-tertiary sm:inline">
                ⌘K
              </kbd>
            </button>

            <div className="flex shrink-0 items-center gap-2">
              {contextSwitcher}
              {headerExtras}
              {showThemeToggle ? (
                <ThemeToggle
                  ariaLabelLight={
                    a11yLabels?.themeLight ?? "Switch to light mode"
                  }
                  ariaLabelDark={
                    a11yLabels?.themeDark ?? "Switch to dark mode"
                  }
                />
              ) : null}
              {notificationBell}
              {profile ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      className={cn(
                        "flex max-w-[220px] items-center gap-2 rounded-full border border-border py-1 pl-1 pr-2",
                        "transition-colors duration-micro hover:border-border-strong",
                        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                      )}
                      aria-label={a11yLabels?.profileMenu ?? "Profile menu"}
                    >
                      <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary-soft text-[11px] font-semibold text-primary-soft-foreground">
                        {profileInitials}
                      </span>
                      <span className="hidden min-w-0 text-left md:block">
                        <span className="block truncate text-[13px] font-medium leading-tight text-foreground">
                          {profile.name}
                        </span>
                        {profile.role ? (
                          <span className="block truncate text-[11px] leading-tight text-tertiary">
                            {profile.role}
                          </span>
                        ) : null}
                      </span>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56 p-2">
                    {userMenu}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : userMenu ? (
                <div className="hidden lg:block">{userMenu}</div>
              ) : null}
            </div>
          </header>

          <main className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto p-4 md:px-6 md:py-6 lg:px-8">
            {children}
          </main>
        </div>
      </div>

      <CommandPalette
        open={cmdOpen}
        onOpenChange={setCmdOpen}
        items={commandItems}
        labels={commandLabels}
        onSelect={(item) => {
          onCommandSelect?.(item);
          onNavigate(item.href);
        }}
      />
    </div>
  );
}
